import React, { useEffect,  useState, useCallback } from "react";
import { Trip } from "@/types/trip";
import TripCard from "../TripCard";
import { useTranslation } from "react-i18next";
import { useProcessedTrips } from "@/hooks/useProcessedTrips";
import { useIsMobile } from "@/hooks/use-mobile";
import { parseISO, compareDesc } from "date-fns";
import { motion } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";

import { useFeatureFlagStatus } from "@/utils/FeatureFlagProvider";
import { useSpaSafeShowAd } from "@/hooks/useSpaSafeShowAd";
import AdSenseWrapper from "../ads/AdSenseWrapper";
import { getAdSenseConfig } from '@/constants/adsense';
import { GoogleAdsScriptLoader } from "../ads/GoogleAdsScriptLoader";



interface DesktopTripListProps {
  futureTrips: Trip[];
  pastTrips: Trip[];
  handleRefreshTrips?: () => void;
}

export const DesktopTripList = ({ futureTrips, pastTrips, handleRefreshTrips }: DesktopTripListProps) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();

  const { processedFutureTrips, processedPastTrips, isProcessing } = useProcessedTrips(futureTrips, pastTrips);
  const { publisherId, slotId } = getAdSenseConfig(isMobile ? '350_250_Responsive' : '728_90_Responsive');
  const { googleAds: googleAdsEnabled } = useFeatureFlagStatus(); // Use boolean flag
  const minHeight = isMobile ? '250px' : '90px';
      console.log("DesktopTripList: googleAdsEnabled", googleAdsEnabled);
  useEffect(() => {
      console.log("DesktopTripList: googleAdsEnabled", googleAdsEnabled);
  }, [googleAdsEnabled]);


  const sortFutureTrips = (a: Trip, b: Trip) => {
    const aStart = a.elements?.[0]?.startDate ? parseISO(a.elements[0].startDate) : null;
    const bStart = b.elements?.[0]?.startDate ? parseISO(b.elements[0].startDate) : null;
    return aStart && bStart ? compareDesc(aStart, bStart) : (b.remainingDays || 0) - (a.remainingDays || 0);
  };

  const sortPastTrips = (a: Trip, b: Trip) => {
    const aEnd = a.elements?.at(-1)?.endDate ? parseISO(a.elements.at(-1)!.endDate) : null;
    const bEnd = b.elements?.at(-1)?.endDate ? parseISO(b.elements.at(-1)!.endDate) : null;
    return aEnd && bEnd ? compareDesc(aEnd, bEnd) : 0;
  };

  const handleTripCardClick = useCallback(
    (trip: Trip) => {
      if (!isMobile) {
        navigate("/trip-timeline", { state: { trip } });
      }
    },
    [isMobile, navigate]
  );

  // --- AdSense script load state ---
  const showAd = useSpaSafeShowAd(location.pathname);

  if (isProcessing) {
    return (
      <div className="px-4 py-6 max-w-5xl mx-auto">
        <div className="flex items-center justify-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-travel-500" />
        </div>
      </div>
    );
  }
  const sortedFutureTrips = Array.isArray(processedFutureTrips) ? [...processedFutureTrips].sort(sortFutureTrips) : [];
  const sortedPastTrips = Array.isArray(processedPastTrips) ? [...processedPastTrips].sort(sortPastTrips) : [];
  const allTripsInOrder = [...sortedFutureTrips, ...sortedPastTrips];

  // display ad when there are no trips added
  if (allTripsInOrder.length === 0) {
    return (
      <div className="px-4 py-6 max-w-5xl mx-auto">
      {googleAdsEnabled === true && showAd && (
        <div style={{ width: '100%',  minHeight: minHeight, }}>
          <AdSenseWrapper
            style={{ width: '100%', minWidth: 0,  display: 'block', margin: '16px 0 24px 0' }}
            slot={slotId}
            publisherId={publisherId}
          />
        </div>
      )}
        <div className="text-center py-12">
          <p className="text-gray-500">{t("index.noTrips", "No trips found")}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <GoogleAdsScriptLoader enabled={googleAdsEnabled} publisherId={publisherId} />
      <div className="px-4 py-6 max-w-5xl mx-auto">
        <div className="grid grid-cols-1 gap-4">
          {allTripsInOrder.map((trip, index) => (
            <React.Fragment key={trip.id}>
              <motion.div
                className="p-2 cursor-pointer"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                onClick={() => handleTripCardClick(trip)}
              >
                <TripCard trip={trip} handleRefreshTrips={handleRefreshTrips} />
              </motion.div>

              {/* Show ad after the first trip only */}
              {index === 0 && googleAdsEnabled === true && showAd && (
                <div
                  className="desktoptrip-list-ad"
                  style={{
                    width: '100%',
                    maxWidth: '100%',
                    minHeight: minHeight,
                    minWidth: 0,
                    display: 'block',
                    margin: '16px auto 32px auto',
                    overflow: 'hidden',
                    boxSizing: 'border-box',
                  }}
                >
                  <AdSenseWrapper
                    style={{
                      width: '100%',
                      maxWidth: '100%',
                      display: 'block',
                      overflow: 'hidden',
                      objectFit: 'contain',
                      boxSizing: 'border-box',
                    }}
                    slot={slotId}
                    publisherId={publisherId}
                  />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </>
  );
};
